import React, { useCallback, useEffect, useState, useEffectEvent } from 'react'

import { Autocomplete, Box, TextField, Typography } from '@mui/material'
import { useMutation } from '~/shared/hooks/useMutation'
import { physician, type PhysicianListResponse } from '@fireflyhealth/api'

type Physician = PhysicianListResponse[number]
import { Loader } from '~/shared/ui'
import { listEventsForProvider } from './ScheduleService'
import { useLocalStorage } from '~/shared/hooks/useLocalStorage'
import { useLocation } from 'react-router'
import { useQueryParam } from '~/shared/hooks/useQueryParam'
import { Switch } from '@mui/material'
import { useLocalStorageBackedToggle } from '~/shared/hooks/useLocalStorageBackedToggle'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/react/timegrid'
import dayGridPlugin from '@fullcalendar/react/daygrid'
import classicThemePlugin from '@fullcalendar/react/themes/classic'
import '@fullcalendar/react/skeleton.css'
import '@fullcalendar/react/themes/classic/theme.css'
import '@fullcalendar/react/themes/classic/palette.css'
import classNames from 'classnames'
import type { Theme } from '@mui/material/styles'

const ProviderScheduleFullCalendar: React.FC = () => {
  const [physicianFilter, setPhysicianFilter] = useState<Record<string, Physician>>({})

  const [selectedPhysician, setSelectedPhysician] = useQueryParam<string | undefined>(
    'physician',
    undefined,
  )
  const [__, setScheduleParams] = useLocalStorage('scheduleParams')
  const [isTimezoneOverrideEnabled, setIsTimezoneOverrideEnabled] = useLocalStorageBackedToggle(
    'isTimezoneOverrideEnabled',
  )

  const [showLoader, setShowLoader] = useState(false)

  const onPhysicianChange = (selected: string) => {
    setShowLoader(true)
    if (selected && physicianFilter[selected]) {
      setSelectedPhysician(selected)
    } else {
      setSelectedPhysician('')
    }
    setShowLoader(false)
  }

  const location = useLocation()
  // Remembers the schedule's URL, and drops a physician in it who isn't among the loaded ones.
  // The query-param setter is rebuilt every render, so the effect reaches it through an event.
  const clearSelectedPhysician = useEffectEvent(() => setSelectedPhysician(''))
  useEffect(() => {
    setScheduleParams(location.search)
    const hasLoadedPhysicians = Object.keys(physicianFilter).length > 0
    if (selectedPhysician && hasLoadedPhysicians && !physicianFilter[selectedPhysician]) {
      clearSelectedPhysician()
    }
  }, [setScheduleParams, location.search, selectedPhysician, physicianFilter])

  const fetchPhysicians = useCallback(async () => {
    const { data: result } = await physician.list()
    const physicianData: Record<string, Physician> = {}
    result.forEach(physician => {
      const physicianName = physician.first_name + ' ' + physician.last_name
      physicianData[physicianName] = physician
    })
    setPhysicianFilter(physicianData)
    return result
  }, [])

  const { loading: loadingPhysicians, handler: loadPhysicians } = useMutation(fetchPhysicians)

  const onToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsTimezoneOverrideEnabled(event.target.checked)
  }

  useEffect(() => {
    void loadPhysicians()
  }, [loadPhysicians])

  const selectedProviderPk = selectedPhysician
    ? physicianFilter[selectedPhysician]?.provider?.pk
    : undefined

  return (
    <>
      {(loadingPhysicians || showLoader) && <Loader />}
      <Box sx={{ display: 'flex', padding: 2 }}>
        <Box sx={{ m: 2, flexGrow: 1 }}>
          <Autocomplete<string>
            size="small"
            options={Object.keys(physicianFilter).sort()}
            value={selectedPhysician}
            renderInput={params => <TextField {...params} label="Choose Provider" />}
            onChange={(_, selected) => {
              onPhysicianChange(selected || '')
            }}
            style={{ width: 280 }}
          />
        </Box>
        <Box sx={{ m: 2 }}>
          <Switch
            checked={isTimezoneOverrideEnabled}
            onChange={onToggle}
            name="isTimezoneOverrideEnabledSwitch"
          />
          View in ET
        </Box>
      </Box>
      {!loadingPhysicians && selectedPhysician == '' && (
        <Box sx={{ ml: 2 }}>
          <Typography>Please choose provider to display the appointment details</Typography>
        </Box>
      )}
      {selectedProviderPk && (
        <Box sx={calendarSx}>
          <FullCalendar
            timeZone={!isTimezoneOverrideEnabled ? 'local' : 'America/New_York'}
            height={'100%'}
            initialView="timeGridWeek"
            views={{
              timeGridWeek: { titleFormat: { year: 'numeric', month: 'short', day: 'numeric' } },
            }}
            weekends={true}
            navLinks={true}
            nowIndicator={true}
            headerToolbar={{
              left: 'prev,next,today',
              center: 'title',
              right: 'timeGridDay,timeGridWeek,dayGridMonth',
            }}
            plugins={[timeGridPlugin, dayGridPlugin, classicThemePlugin]}
            eventClass={info =>
              classNames(
                `schedule-event-${String(info.event.extendedProps.variant)}`,
                info.isPast && 'schedule-event-past',
              )
            }
            eventInnerClass="schedule-event-inner"
            eventTimeClass={info =>
              classNames('schedule-event-time', info.isShort && 'schedule-event-time-short')
            }
            eventTitleClass="schedule-event-title"
            events={async function (info, successCallback, _failureCallback) {
              // uses start and end from the calendar to fetch events from the backend
              successCallback(await listEventsForProvider(selectedProviderPk, info.start, info.end))
            }}
            eventClick={function (info) {
              // don't let the browser navigate
              // so that we can open the appointments tab in a new URL
              info.jsEvent.preventDefault()
              if (info.event.url) {
                window.open(info.event.url)
              }
            }}
          />
        </Box>
      )}
    </>
  )
}

// FullCalendar's classic theme stylesheet loads after emotion's, so `!important` keeps these.
const calendarSx = (theme: Theme) => {
  const eventStyles: Record<string, object> = {}
  for (const [variant, colors] of Object.entries(theme.palette.schedule)) {
    eventStyles[`& .schedule-event-${variant}`] = {
      borderColor: `${colors.border} !important`,
      backgroundColor: `${colors.background} !important`,
    }
  }
  return {
    height: '80vh',
    ml: 1,
    mr: 1,
    ...eventStyles,
    '& .schedule-event-past': { opacity: 0.5 },
    '& .schedule-event-inner': { color: `${theme.palette.navy[500]} !important` },
    '& .schedule-event-time': { fontSize: '1rem !important', fontWeight: '700' },
    '& .schedule-event-time-short::after': { content: '", "', whiteSpace: 'pre' },
    '& .schedule-event-title': { fontSize: '1rem !important', fontWeight: '600' },
  }
}

export default ProviderScheduleFullCalendar
